import { Form, Formik } from 'formik';

import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

import { EMPTY_OBJECT } from '@e-socna/empty';
import type { OrientationEnum, PaperFormatEnum } from '@e-socna/data-layer';

import { getTouchedError } from '../../utils/get-touched-error.util';
import { LazyEditor } from '../../components/Editor/Lazy';
import { ConfirmBeforeExitForm } from '../../components/ConfirmBeforeExitForm';
import { FORM_LAYOUT_STYLE, FormLayout } from '../../components/FormLayout';
import { useUiContext } from '../../contexts/UiContext/hooks/useUiContext.hook';
import { useDocumentEditorContext } from '../../components/DocumentEditor/contexts/DocumentEditorContext/hooks/useDocumentEditorContext.hook';
import type { TextualContent } from '../../components/DocumentEditor/types/textual-content.type';
import type { FileStorage } from '../../components/Editor/types/file-storage.type';

import { FormVariant } from '../enums/variant.enum';

import { useInitialValuesFactory } from './hooks/useInitialValuesFactory.hook';
import { SpecialSectionSchema } from './schemas/special-section.schema';
import { useSpecialSectionSubmit } from './hooks/useSubmit.hook';
import type { SubmitHandler } from './types/submit-handler.type';

interface Props {
  readonly paperFormat?: PaperFormatEnum;
  readonly formVariant?: FormVariant;
  readonly initialValues?: TextualContent;
  readonly onCancel: () => void;
  readonly onSubmit: SubmitHandler;
  readonly orientation?: OrientationEnum;
  readonly setMustBlockAction: (value: boolean) => void;
  readonly storage: FileStorage;
}

export const SpecialSectionForm = ({
  paperFormat,
  formVariant,
  initialValues: pInitialValues,
  onCancel: handleCancel,
  onSubmit: pHandleSubmit,
  orientation,
  setMustBlockAction,
  storage,
}: Readonly<Props>) => {
  const { ui } = useUiContext();

  const {
    props: { resolversSettings },
  } = useDocumentEditorContext();

  const { handleSubmit } = useSpecialSectionSubmit({
    handleSubmit: pHandleSubmit,
  });

  const { initialValues } = useInitialValuesFactory(pInitialValues);

  const submitLabel =
    !formVariant || formVariant === FormVariant.Creation
      ? 'Insérer'
      : 'Modifier';

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={SpecialSectionSchema}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit: formikSubmit,
        isSubmitting,
        isValid,
        touched,
        values,
      }) => (
        <Form
          onSubmit={formikSubmit}
          // eslint-disable-next-line react/forbid-component-props
          style={FORM_LAYOUT_STYLE}
        >
          <ConfirmBeforeExitForm setMustBlockAction={setMustBlockAction} />
          <FormLayout
            actions={
              <Stack direction="row" spacing={2}>
                <Button
                  disabled={!isValid}
                  loading={isSubmitting || ui.isBackgroundOperationRunning}
                  size="large"
                  type="submit"
                  variant="contained"
                >
                  {submitLabel}
                </Button>
                <Button onClick={handleCancel} size="large" variant="outlined">
                  Annuler
                </Button>
              </Stack>
            }
            title={`${submitLabel} une section`}
          >
            <Grid
              container
              spacing={2}
              // The editor's 100% height needs a definite height chain, and a
              // wrapping column would move the editor to a second column.
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexWrap: 'nowrap',
                height: '100%',
              }}
            >
              <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                <TextField
                  error={!!(errors.title && touched.title)}
                  fullWidth
                  helperText={getTouchedError(errors, touched, 'title')}
                  id="section-title"
                  label="Titre"
                  name="title"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  value={values.title}
                  variant="outlined"
                />
              </Grid>
              <Grid
                size={12}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flexGrow: 1,
                  pt: 1,
                  pb: 1,
                }}
              >
                <LazyEditor
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...(errors.content
                    ? {
                        alert: {
                          severity: 'error',
                          content: errors.content,
                        },
                      }
                    : EMPTY_OBJECT)}
                  disabled={isSubmitting}
                  error={!!(errors.content && touched.content)}
                  id={values._id}
                  initialValue={initialValues.content}
                  label="Contenu"
                  name="content"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  orientation={orientation}
                  paperFormat={paperFormat}
                  required={false}
                  resolversSettings={resolversSettings}
                  storage={storage}
                  value={values.content}
                />
              </Grid>
            </Grid>
          </FormLayout>
        </Form>
      )}
    </Formik>
  );
};
